import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { ErrorDisplay } from '../../common';
import { useToggle } from '../../../hooks';
import useFetchCampaignQuestions from './hooks/useFetchCampaignQuestions';
import QuestionsEditorForm from './edit/QuestionsEditorForm';
import QuestionsEditorView from './view/QuestionsEditorView';
import CopyQuestionsManager from './edit/CopyQuestionsManager';
import { CampaignQuestionsEditorProvider } from './context';

import type { CampaignQuestion } from './types';

interface CampaignQuestionsEditorProps {
    campaignId: number | null;
}

/**
 * Main component for managing campaign questions. It displays the questions in view mode and allows to edit them.
 *
 * @param {CampaignQuestionsEditorProps} props
 * @returns {JSX.Element}
 */
const CampaignQuestionsEditor: React.FC<CampaignQuestionsEditorProps> = ({ campaignId = null }) => {
    const [isCopyDialogOpen, , setIsCopyDialogOpen] = useToggle(false);
    const [isEditMode, , setIsEditMode] = useToggle(false);

    const {
        data: fetchedQuestions = [] as CampaignQuestion[],
        isLoading: isLoadingQuestions,
        isError,
        errorMessage,
    } = useFetchCampaignQuestions(campaignId);

    if (isLoadingQuestions) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return <ErrorDisplay message={errorMessage || 'Failed to load questions'} />;
    }

    const questions = Array.isArray(fetchedQuestions) ? fetchedQuestions : [];

    if (isEditMode) {
        return (
            <CampaignQuestionsEditorProvider>
                <QuestionsEditorForm
                    campaignId={campaignId}
                    initialQuestions={questions}
                    onOpenCopyDialog={() => setIsCopyDialogOpen(true)}
                    onCancel={() => setIsEditMode(false)}
                />
                <CopyQuestionsManager
                    isOpen={isCopyDialogOpen}
                    onClose={() => setIsCopyDialogOpen(false)}
                    campaignId={campaignId}
                    initialQuestions={questions}
                />
            </CampaignQuestionsEditorProvider>
        );
    }

    return <QuestionsEditorView questions={questions} onToggleEditMode={() => setIsEditMode(true)} />;
};

export default CampaignQuestionsEditor;
